import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/utils/supabase';
import { useAuth } from '@/contexts/useAuth';
import {
  cancelMyCompRequest,
  compApprove,
  compReject,
  CompensationServiceError,
  formatCompRequestStatus
} from '@/services/compensationService';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('ObCompRequestsPanel', 'compensation');

interface CompChangeRequest {
  id: string;
  target_user_id: string;
  requesting_user_id: string;
  target_schedule_id: number;
  target_schedule_name: string;
  target_percentage: number | null;
  target_max_cap: number | null;
  product_categories_snapshot: string[];
  scope_label: 'core' | 'full_footprint';
  effective_date: string;
  status: 'pending' | 'approved' | 'applied' | 'rejected' | 'failed' | 'cancelled' | 'needs_review';
  justification: string | null;
  approved_at: string | null;
  applied_at: string | null;
  rejected_at: string | null;
  attempt_count: number;
  last_failure_reason: string | null;
  failed_reason: string | null;
  created_at: string;
}

interface ObCompRequestsPanelProps {
  /**
   * Audience for this mount.
   * - 'self': render the LO/admin viewing their own requests; show Cancel
   *   only on owned, scheduled, future-dated rows. Never show
   *   Approve/Re-approve/Reject.
   * - 'admin': render the org-wide admin surface; show Approve (legacy
   *   pending), Re-approve (needs_review/failed), and Reject. Never show
   *   Cancel.
   *
   * Required — there is no safe default. Inferring from a userId match
   * would let an admin viewing an LO profile look like the request owner.
   */
  mode: 'self' | 'admin';
  /** When set, only requests for this user are shown. Omit to show all org requests (admin org-wide view). */
  userId?: string;
  onChange?: () => void;
}

function formatMoney(n: number | null | undefined): string {
  if (n == null || n === 0) {
return 'No cap';
}
  return `$${n.toLocaleString()}`;
}

function formatPct(n: number | null | undefined): string {
  return n == null ? '—' : `${n}%`;
}

function statusVariant(status: CompChangeRequest['status']): 'default' | 'secondary' | 'outline' | 'destructive' {
  switch (status) {
    case 'pending':
      return 'secondary';
    case 'approved':
      return 'default';
    case 'applied':
      return 'default';
    case 'needs_review':
    case 'failed':
    case 'rejected':
    case 'cancelled':
      return 'destructive';
    default:
      return 'outline';
  }
}

// today_LA derived once per render to drive future-date Cancel-eligibility.
// Mirrors the helper in compensationService so the UI gate matches what the
// server would accept.
function todayLaDateString(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(new Date());
}

export function ObCompRequestsPanel({ mode, userId, onChange }: ObCompRequestsPanelProps): JSX.Element {
  const { user } = useAuth();
  const currentUserId = user?.id ?? null;

  const [requests, setRequests] = useState<CompChangeRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [rejectDialog, setRejectDialog] = useState<{ requestId: string; reason: string } | null>(null);

  const today = useMemo(() => todayLaDateString(), [requests]);

  const load = async () => {
    setLoading(true);
    let q = supabase
      .from('compensation_change_requests')
      .select('*')
      .order('created_at', { ascending: false });
    if (userId) {
q = q.eq('target_user_id', userId);
} else {
q = q.in('status', ['pending', 'approved', 'needs_review', 'failed']);
}
    const { data, error } = await q;
    if (error) {
      log.error('Failed to load compensation_change_requests', { userId }, error);
      toast.error('Failed to load comp change requests');
      setLoading(false);
      return;
    }
    setRequests((data ?? []) as CompChangeRequest[]);
    setLoading(false);
  };

  useEffect(() => {
    void load();
  }, [userId]);

  const toggleExpanded = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
next.delete(id);
} else {
next.add(id);
}
      return next;
    });
  };

  const handleApprove = async (req: CompChangeRequest) => {
    setPendingId(req.id);
    try {
      await compApprove(req.id);
      toast.success(`Request approved for processing — target date ${req.effective_date}`);
      await load();
      onChange?.();
    } catch (e) {
      const msg = e instanceof CompensationServiceError ? e.message : (e as Error).message;
      toast.error(`Approve failed: ${msg}`);
    } finally {
      setPendingId(null);
    }
  };

  const handleReject = async () => {
    if (!rejectDialog) {
return;
}
    if (!rejectDialog.reason.trim()) {
      toast.error('Reason is required');
      return;
    }
    setPendingId(rejectDialog.requestId);
    try {
      await compReject(rejectDialog.requestId, rejectDialog.reason.trim());
      toast.success('Request rejected');
      setRejectDialog(null);
      await load();
      onChange?.();
    } catch (e) {
      const msg = e instanceof CompensationServiceError ? e.message : (e as Error).message;
      toast.error(`Reject failed: ${msg}`);
    } finally {
      setPendingId(null);
    }
  };

  const handleCancel = async (req: CompChangeRequest) => {
    setPendingId(req.id);
    try {
      await cancelMyCompRequest(req.id);
      toast.success(
        `Request cancelled — your ${req.target_schedule_name} change for ${req.effective_date} is no longer scheduled`
      );
      await load();
      onChange?.();
    } catch (e) {
      const msg = e instanceof CompensationServiceError ? e.message : (e as Error).message;
      toast.error(`Cancel failed: ${msg}`);
    } finally {
      setPendingId(null);
    }
  };

  const visible = requests.filter((r) => userId || ['pending', 'approved', 'needs_review', 'failed'].includes(r.status));

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>
            {userId ? 'Compensation Change Requests' : 'Open Compensation Change Requests'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : visible.length === 0 ? (
            <p className="text-sm text-muted-foreground">No requests.</p>
          ) : (
            <div className="space-y-3">
              {visible.map((req) => {
                const open = expanded.has(req.id);
                // Admin-only actions are split:
                //   Approve / Re-approve covers pending (legacy clear-out),
                //     needs_review (drift recovery), and failed (retry).
                //   Reject is the admin override on ANY non-terminal request,
                //     including `approved` (scheduled). The server admin
                //     branch in comp-reject explicitly allows the same set,
                //     so the UI must mirror that to keep the override
                //     available for self-serve scheduled rows.
                const adminCanApprove =
                  mode === 'admin' &&
                  ['pending', 'needs_review', 'failed'].includes(req.status);
                const adminCanReject =
                  mode === 'admin' &&
                  ['pending', 'approved', 'needs_review', 'failed'].includes(req.status);
                const adminApproveLabel = req.status === 'pending' ? 'Approve' : 'Re-approve';

                // Self-cancel mirrors the server-side guard exactly: caller
                // owns both sides of the request, status is `approved`, and
                // effective_date is strictly in the future (LA).
                const canSelfCancel =
                  mode === 'self' &&
                  currentUserId != null &&
                  currentUserId === req.requesting_user_id &&
                  currentUserId === req.target_user_id &&
                  req.status === 'approved' &&
                  (req.effective_date ?? '').slice(0, 10) > today;

                return (
                  <div key={req.id} className="rounded border">
                    <Collapsible open={open} onOpenChange={() => toggleExpanded(req.id)}>
                      <div className="flex items-center justify-between p-3">
                        <CollapsibleTrigger className="flex items-center gap-2 text-left">
                          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          <div>
                            <div className="font-medium">{req.target_schedule_name}</div>
                            <div className="text-sm text-muted-foreground">
                              {formatPct(req.target_percentage)} · cap {formatMoney(req.target_max_cap)} · effective {req.effective_date}
                            </div>
                          </div>
                        </CollapsibleTrigger>
                        <div className="flex items-center gap-2">
                          <Badge variant={statusVariant(req.status)}>
                            {formatCompRequestStatus(req.status, req.effective_date, req.last_failure_reason)}
                          </Badge>
                          <Badge variant="outline">{req.scope_label}</Badge>
                          {adminCanApprove && (
                            <Button
                              size="sm"
                              onClick={() => handleApprove(req)}
                              disabled={pendingId === req.id}
                            >
                              {adminApproveLabel}
                            </Button>
                          )}
                          {adminCanReject && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setRejectDialog({ requestId: req.id, reason: '' })}
                              disabled={pendingId === req.id}
                            >
                              Reject
                            </Button>
                          )}
                          {canSelfCancel && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleCancel(req)}
                              disabled={pendingId === req.id}
                            >
                              Cancel
                            </Button>
                          )}
                        </div>
                      </div>
                      <CollapsibleContent>
                        <div className="px-3 pb-3 pt-0 space-y-2 text-sm">
                          <div>
                            <span className="text-muted-foreground">Snapshot categories ({req.product_categories_snapshot.length}):</span>{' '}
                            <span className="font-mono text-xs">{req.product_categories_snapshot.join(', ')}</span>
                          </div>
                          {req.justification && (
                            <div>
                              <span className="text-muted-foreground">Justification:</span> {req.justification}
                            </div>
                          )}
                          {req.last_failure_reason && (
                            <div className="text-destructive">
                              <span className="font-medium">Last failure:</span> {req.last_failure_reason}
                              {req.attempt_count > 0 && (
                                <span className="ml-2 text-xs text-muted-foreground">
                                  (attempt {req.attempt_count}/5)
                                </span>
                              )}
                            </div>
                          )}
                          {req.failed_reason && req.failed_reason !== req.last_failure_reason && (
                            <div className="text-destructive">
                              <span className="font-medium">Reason:</span> {req.failed_reason}
                            </div>
                          )}
                          <div className="text-xs text-muted-foreground">
                            Created {new Date(req.created_at).toLocaleString()}
                            {req.approved_at && <> · approved {new Date(req.approved_at).toLocaleString()}</>}
                            {req.applied_at && <> · applied {new Date(req.applied_at).toLocaleString()}</>}
                          </div>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={rejectDialog != null} onOpenChange={(o) => !o && setRejectDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Compensation Change Request</DialogTitle>
            <DialogDescription>
              The reason is shown to the requester and stored in the audit trail.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reject-reason">Reason</Label>
            <Textarea
              id="reject-reason"
              value={rejectDialog?.reason ?? ''}
              onChange={(e) =>
                setRejectDialog((prev) => (prev ? { ...prev, reason: e.target.value } : null))
              }
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog(null)} disabled={pendingId != null}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleReject} disabled={pendingId != null}>
              {pendingId ? 'Rejecting…' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
